import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  MapPin,
  PencilLine,
  Phone,
  Plus,
  Trash2,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  EmptyState,
  PanelActionButton,
  SectionPanel,
  StatusPill,
} from "@/components/platform/ContentPrimitives";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  AGENDA_CATEGORY_OPTIONS,
  AGENDA_COST_OPTIONS,
  AGENDA_TYPE_OPTIONS,
} from "@/lib/polenica";
import { formatDateTime, splitLines } from "@/lib/formatters";

const DAY_LABELS = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];
const MONTH_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export const AGENDA_CATEGORY_STYLES = {
  "Genero / participacion de mujeres": "border-[#C35C87]/30 bg-[#F7DCE8] text-[#73304E]",
  "Derechos humanos": "border-[#C46B45]/30 bg-[#F8E3D7] text-[#713A25]",
  Diversidad: "border-[#755AA8]/30 bg-[#E9E0F5] text-[#49366D]",
  "Juventud / ninez": "border-[#D39B31]/30 bg-[#F8EDC9] text-[#695018]",
  "Medio ambiente y justicia climatica": "border-[#4B8A58]/30 bg-[#DCEEDC] text-[#295735]",
  "Comunidad / participacion ciudadana": "border-[#3D7E91]/30 bg-[#DCECF0] text-[#245260]",
};

export function getAgendaCategoryStyle(category) {
  return AGENDA_CATEGORY_STYLES[category] || "border-primary/15 bg-primary/10 text-primary";
}

const EMPTY_FORM = {
  tipo: "encuentro",
  nombre: "",
  fecha_inicio: "",
  fecha_fin: "",
  lugar: "",
  municipio: "Medellin",
  comuna: "",
  categoria: AGENDA_CATEGORY_OPTIONS[0],
  costo: AGENDA_COST_OPTIONS[0],
  descripcion: "",
  iniciativa_organizadora: "",
  contacto_nombre: "",
  contacto_celular: "",
  contacto_correo: "",
  imagenes: "",
};

function baseInputClass() {
  return "h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/35";
}

function textAreaClass() {
  return "w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 py-3 outline-none transition focus:border-primary/35";
}

function InputField({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-primary">{label}</span>
      {children}
      {hint ? <span className="mt-2 block text-xs text-mutedForeground">{hint}</span> : null}
    </label>
  );
}

function buildPayload(form) {
  return {
    ...form,
    imagenes: splitLines(form.imagenes).slice(0, 5),
    fecha_fin: form.fecha_fin || null,
  };
}

function eventToForm(event) {
  return {
    tipo: event.tipo || "encuentro",
    nombre: event.nombre || "",
    fecha_inicio: event.fecha_inicio ? event.fecha_inicio.slice(0, 16) : "",
    fecha_fin: event.fecha_fin ? event.fecha_fin.slice(0, 16) : "",
    lugar: event.lugar || "",
    municipio: event.municipio || "Medellin",
    comuna: event.comuna || "",
    categoria: event.categoria || AGENDA_CATEGORY_OPTIONS[0],
    costo: event.costo || AGENDA_COST_OPTIONS[0],
    descripcion: event.descripcion || "",
    iniciativa_organizadora: event.iniciativa_organizadora || "",
    contacto_nombre: event.contacto_nombre || "",
    contacto_celular: event.contacto_celular || "",
    contacto_correo: event.contacto_correo || "",
    imagenes: Array.isArray(event.imagenes) ? event.imagenes.join("\n") : "",
  };
}

function getTypeLabel(value) {
  return AGENDA_TYPE_OPTIONS.find((option) => option.value === value)?.label || value || "Otro";
}

function getDateKey(dateLike) {
  const date = new Date(dateLike);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildMonthGrid(month, year) {
  const firstDay = new Date(year, month - 1, 1);
  const firstWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];

  for (let index = 0; index < firstWeekday; index += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month - 1, day));
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
}

function updateSearchParam(setSearchParams, key, value) {
  setSearchParams((current) => {
    const next = new URLSearchParams(current);
    if (value == null || value === "") {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    return next;
  });
}

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [tipoFilter, setTipoFilter] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("");
  const [costoFilter, setCostoFilter] = useState("");
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedDate, setSelectedDate] = useState(getDateKey(now));
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showEventForm, setShowEventForm] = useState(Boolean(editId));

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsResponse, myEventsResponse] = await Promise.all([
        api.get("/events", {
          params: {
            month,
            year,
            tipo: tipoFilter || undefined,
            categoria: categoriaFilter || undefined,
            costo: costoFilter || undefined,
          },
        }),
        user ? api.get("/events/my-events") : Promise.resolve({ data: [] }),
      ]);

      setEvents(eventsResponse.data?.items || []);
      setMyEvents(myEventsResponse.data || []);
    } catch (_error) {
      toast.error("No fue posible cargar la agenda huertera.");
    } finally {
      setLoading(false);
    }
  }, [categoriaFilter, costoFilter, month, tipoFilter, user, year]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (!editId) {
      setForm(EMPTY_FORM);
      return;
    }

    setShowEventForm(true);

    const target = myEvents.find((item) => String(item.id) === String(editId));
    if (target) {
      setForm(eventToForm(target));
      return;
    }

    api
      .get(`/events/${editId}`)
      .then((response) => setForm(eventToForm(response.data)))
      .catch(() => toast.error("No fue posible cargar el evento para editarlo."));
  }, [editId, myEvents]);

  const monthGrid = useMemo(() => buildMonthGrid(month, year), [month, year]);

  const eventsByDay = useMemo(() => {
    return events.reduce((accumulator, item) => {
      const key = getDateKey(item.fecha_inicio);
      if (!accumulator[key]) {
        accumulator[key] = [];
      }
      accumulator[key].push(item);
      return accumulator;
    }, {});
  }, [events]);

  const selectedDayEvents = useMemo(
    () => eventsByDay[selectedDate] || [],
    [eventsByDay, selectedDate],
  );

  useEffect(() => {
    if (!selectedDayEvents.length) {
      setSelectedEventId(null);
      return;
    }

    const selectedExists = selectedDayEvents.some((item) => item.id === selectedEventId);
    if (!selectedExists) {
      setSelectedEventId(null);
    }
  }, [selectedDayEvents, selectedEventId]);

  const selectedEvent = selectedDayEvents.find((item) => item.id === selectedEventId) || null;

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/events/${editId}`, buildPayload(form));
        toast.success("Encuentro actualizado correctamente.");
      } else {
        await api.post("/events", buildPayload(form));
        toast.success("Encuentro creado y publicado correctamente.");
      }

      updateSearchParam(setSearchParams, "edit", null);
      setForm(EMPTY_FORM);
      setShowEventForm(false);
      await fetchEvents();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No fue posible guardar el encuentro.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    setDeletingId(String(eventId));
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Encuentro eliminado.");
      if (String(editId) === String(eventId)) {
        updateSearchParam(setSearchParams, "edit", null);
        setForm(EMPTY_FORM);
      }
      await fetchEvents();
    } catch (_error) {
      toast.error("No fue posible eliminar el encuentro.");
    } finally {
      setDeletingId("");
    }
  };

  const handleMoveMonth = (direction) => {
    const current = new Date(year, month - 1, 1);
    current.setMonth(current.getMonth() + direction);
    setMonth(current.getMonth() + 1);
    setYear(current.getFullYear());
  };

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="grid gap-6">
        <SectionPanel
          title={`${MONTH_LABELS[month - 1]} ${year}`}
          eyebrow="Consulta publica"
          description="La agenda se actualiza por mes y conserva filtros por tipo, costo y categoria."
          actions={
            <>
              <PanelActionButton tone="secondary" onClick={() => handleMoveMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
                Mes anterior
              </PanelActionButton>
              <PanelActionButton tone="secondary" onClick={() => handleMoveMonth(1)}>
                Mes siguiente
                <ChevronRight className="h-4 w-4" />
              </PanelActionButton>
              <PanelActionButton
                onClick={() => {
                  if (!user) {
                    navigate(`/login?next=${encodeURIComponent("/calendario")}`);
                    return;
                  }
                  setShowEventForm(true);
                  window.setTimeout(() => document.getElementById("gestion-eventos")?.scrollIntoView({ behavior: "smooth" }), 0);
                }}
              >
                <Plus className="h-4 w-4" />
                Ingresar un evento
              </PanelActionButton>
            </>
          }
        >
          <div className="grid gap-4 md:grid-cols-5">
            <InputField label="Mes">
              <select
                value={month}
                onChange={(event) => setMonth(Number(event.target.value))}
                className={baseInputClass()}
              >
                {MONTH_LABELS.map((label, index) => (
                  <option key={label} value={index + 1}>
                    {label}
                  </option>
                ))}
              </select>
            </InputField>

            <InputField label="Ano">
              <input
                value={year}
                onChange={(event) => setYear(Number(event.target.value) || now.getFullYear())}
                className={baseInputClass()}
              />
            </InputField>

            <InputField label="Tipo">
              <select
                value={tipoFilter}
                onChange={(event) => setTipoFilter(event.target.value)}
                className={baseInputClass()}
              >
                <option value="">Todos</option>
                {AGENDA_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputField>

            <InputField label="Costo">
              <select
                value={costoFilter}
                onChange={(event) => setCostoFilter(event.target.value)}
                className={baseInputClass()}
              >
                <option value="">Todos</option>
                {AGENDA_COST_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </InputField>

            <InputField label="Categoria">
              <select
                value={categoriaFilter}
                onChange={(event) => setCategoriaFilter(event.target.value)}
                className={baseInputClass()}
              >
                <option value="">Todas</option>
                {AGENDA_CATEGORY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </InputField>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" aria-label="Colores por categoría">
            {AGENDA_CATEGORY_OPTIONS.map((category) => (
              <span key={category} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${getAgendaCategoryStyle(category)}`}>
                {category}
              </span>
            ))}
          </div>

          <div className="platform-soft-panel mt-6 rounded-[28px] border border-primary/10 p-4">
            <div className="grid grid-cols-7 gap-2">
              {DAY_LABELS.map((label) => (
                <div
                  key={label}
                  className="rounded-2xl px-2 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-primary/70"
                >
                  {label}
                </div>
              ))}

              {monthGrid.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="min-h-[118px] rounded-2xl bg-transparent" />;
                }

                const dayKey = getDateKey(date);
                const items = eventsByDay[dayKey] || [];
                const selected = selectedDate === dayKey;
                const today = dayKey === getDateKey(new Date());

                return (
                  <button
                    key={dayKey}
                    type="button"
                    onClick={() => setSelectedDate(dayKey)}
                    className={`min-h-[118px] rounded-[22px] border p-3 text-left transition ${
                      selected
                        ? "border-primary bg-primary text-primaryForeground shadow-[0_16px_28px_rgba(44,78,54,0.18)]"
                        : "border-primary/10 bg-white text-textMain hover:border-primary/30"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">{date.getDate()}</span>
                      {today ? (
                        <span
                          className={`rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                            selected ? "bg-white/15" : "bg-primary/10 text-primary"
                          }`}
                        >
                          Hoy
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-3 space-y-2">
                      {items.slice(0, 2).map((item) => (
                        <div
                          key={item.id}
                          className={`rounded-xl border px-2 py-2 text-xs leading-5 ${getAgendaCategoryStyle(item.categoria)}`}
                        >
                          {item.nombre}
                        </div>
                      ))}
                      {items.length > 2 ? (
                        <div className={`text-xs font-semibold ${selected ? "text-primaryForeground/85" : "text-primary/75"}`}>
                          +{items.length - 2} mas
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6">
            {!loading && selectedDayEvents.length ? (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-primary">
                  Eventos del {selectedDate}
                </p>
                {selectedDayEvents.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedEventId(item.id)}
                    className={`flex w-full items-start justify-between gap-4 rounded-[22px] border px-4 py-4 text-left transition ${
                      selectedEvent?.id === item.id
                        ? "border-primary bg-primary text-primaryForeground"
                        : "border-primary/10 bg-white text-textMain hover:border-primary/25"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold">{item.nombre}</p>
                      <p className={`mt-2 text-xs ${selectedEvent?.id === item.id ? "text-primaryForeground/85" : "text-mutedForeground"}`}>
                        {getTypeLabel(item.tipo)}
                      </p>
                    </div>
                    <span className={`text-xs ${selectedEvent?.id === item.id ? "text-primaryForeground/85" : "text-mutedForeground"}`}>
                      {new Date(item.fecha_inicio).toLocaleTimeString("es-CO", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}

            {!loading && !events.length ? (
              <div className="mt-6">
                <EmptyState
                  icon={CalendarDays}
                  title="No hay encuentros publicados en este corte"
                  description="Ajuste los filtros o avance a otro mes para encontrar nuevas actividades."
                />
              </div>
            ) : null}
          </div>
        </SectionPanel>

        {selectedEvent ? <SectionPanel
            title={selectedEvent ? selectedEvent.nombre : "Detalle del encuentro"}
            eyebrow="Lectura rapida"
            description={
              selectedEvent
                ? "Abra el detalle territorial y los canales de contacto visibles de cada encuentro."
                : "Seleccione un dia con eventos para abrir el detalle completo."
            }
          >
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <StatusPill>{getTypeLabel(selectedEvent.tipo)}</StatusPill>
                  {selectedEvent.categoria ? <StatusPill tone="neutral">{selectedEvent.categoria}</StatusPill> : null}
                  {selectedEvent.costo ? <StatusPill tone="neutral">{selectedEvent.costo}</StatusPill> : null}
                </div>

                <div className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-5">
                  <p className="text-sm leading-7 text-textMain">
                    {selectedEvent.descripcion || "Sin descripcion adicional."}
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-[24px] border border-primary/10 bg-white p-5 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">
                      Fecha y hora
                    </p>
                    <p className="mt-3 text-sm leading-6 text-textMain">
                      {formatDateTime(selectedEvent.fecha_inicio)}
                    </p>
                    {selectedEvent.fecha_fin ? (
                      <p className="mt-2 text-sm leading-6 text-mutedForeground">
                        Cierre: {formatDateTime(selectedEvent.fecha_fin)}
                      </p>
                    ) : null}
                  </div>

                  <div className="rounded-[24px] border border-primary/10 bg-white p-5 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">
                      Lugar
                    </p>
                    <p className="mt-3 inline-flex items-center gap-2 text-sm text-textMain">
                      <MapPin className="h-4 w-4 text-primary" />
                      {[selectedEvent.municipio, selectedEvent.comuna, selectedEvent.lugar]
                        .filter(Boolean)
                        .join(" / ") || "Ubicacion por confirmar"}
                    </p>
                    {selectedEvent.iniciativa_organizadora ? (
                      <p className="mt-2 text-sm text-mutedForeground">
                        Iniciativa: {selectedEvent.iniciativa_organizadora}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-[24px] border border-primary/10 bg-white p-5 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">
                    Contacto
                  </p>
                  <div className="mt-3 space-y-2 text-sm text-textMain">
                    <p>{selectedEvent.contacto_nombre || "Contacto no visible"}</p>
                    <p className="inline-flex items-center gap-2">
                      <Phone className="h-4 w-4 text-primary" />
                      {selectedEvent.contacto_celular || "Celular no visible"}
                    </p>
                    <p>{selectedEvent.contacto_correo || "Correo no visible"}</p>
                  </div>
                </div>
              </div>
          </SectionPanel> : null}

          {showEventForm ? <SectionPanel
            id="gestion-eventos"
            title={editId ? "Editar encuentro" : "Publicar encuentro"}
            eyebrow="Gestion propia"
            description="Comparta actividades de la red huertera. Cada cambio queda publicado de inmediato."
          >
            {!user ? (
              <EmptyState
                icon={CalendarDays}
                title="Puede explorar la agenda sin registrarse"
                description="Para publicar, editar o gestionar sus encuentros debe ingresar con su cuenta."
              />
            ) : (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <InputField label="Tipo">
                    <select
                      value={form.tipo}
                      onChange={(event) => handleChange("tipo", event.target.value)}
                      className={baseInputClass()}
                    >
                      {AGENDA_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </InputField>

                  <InputField label="Nombre">
                    <input
                      value={form.nombre}
                      onChange={(event) => handleChange("nombre", event.target.value)}
                      className={baseInputClass()}
                      required
                    />
                  </InputField>

                  <InputField label="Inicio">
                    <input
                      type="datetime-local"
                      value={form.fecha_inicio}
                      onChange={(event) => handleChange("fecha_inicio", event.target.value)}
                      className={baseInputClass()}
                      required
                    />
                  </InputField>

                  <InputField label="Fin">
                    <input
                      type="datetime-local"
                      value={form.fecha_fin}
                      onChange={(event) => handleChange("fecha_fin", event.target.value)}
                      className={baseInputClass()}
                    />
                  </InputField>

                  <InputField label="Categoria">
                    <select
                      value={form.categoria}
                      onChange={(event) => handleChange("categoria", event.target.value)}
                      className={baseInputClass()}
                    >
                      {AGENDA_CATEGORY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </InputField>

                  <InputField label="Costo">
                    <select
                      value={form.costo}
                      onChange={(event) => handleChange("costo", event.target.value)}
                      className={baseInputClass()}
                    >
                      {AGENDA_COST_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </InputField>

                  <InputField label="Lugar">
                    <input
                      value={form.lugar}
                      onChange={(event) => handleChange("lugar", event.target.value)}
                      className={baseInputClass()}
                    />
                  </InputField>

                  <InputField label="Municipio">
                    <input
                      value={form.municipio}
                      onChange={(event) => handleChange("municipio", event.target.value)}
                      className={baseInputClass()}
                      required
                    />
                  </InputField>

                  <InputField label="Comuna o zona">
                    <input
                      value={form.comuna}
                      onChange={(event) => handleChange("comuna", event.target.value)}
                      className={baseInputClass()}
                    />
                  </InputField>

                  <InputField label="Iniciativa organizadora">
                    <input
                      value={form.iniciativa_organizadora}
                      onChange={(event) => handleChange("iniciativa_organizadora", event.target.value)}
                      className={baseInputClass()}
                    />
                  </InputField>

                  <InputField label="Nombre de contacto">
                    <input
                      value={form.contacto_nombre}
                      onChange={(event) => handleChange("contacto_nombre", event.target.value)}
                      className={baseInputClass()}
                    />
                  </InputField>

                  <InputField label="Celular de contacto">
                    <input
                      value={form.contacto_celular}
                      onChange={(event) => handleChange("contacto_celular", event.target.value)}
                      className={baseInputClass()}
                    />
                  </InputField>

                  <InputField label="Correo de contacto">
                    <input
                      type="email"
                      value={form.contacto_correo}
                      onChange={(event) => handleChange("contacto_correo", event.target.value)}
                      className={baseInputClass()}
                    />
                  </InputField>
                </div>

                <InputField label="Descripcion">
                  <textarea
                    value={form.descripcion}
                    onChange={(event) => handleChange("descripcion", event.target.value)}
                    rows={4}
                    className={textAreaClass()}
                  />
                </InputField>

                <InputField label="Imagenes" hint="Pegue hasta 5 URL, una por linea.">
                  <textarea
                    value={form.imagenes}
                    onChange={(event) => handleChange("imagenes", event.target.value)}
                    rows={4}
                    className={textAreaClass()}
                  />
                </InputField>

                <div className="flex flex-wrap gap-3">
                  <PanelActionButton type="submit" disabled={saving}>
                    <Plus className="h-4 w-4" />
                    {saving ? "Guardando..." : editId ? "Actualizar encuentro" : "Publicar encuentro"}
                  </PanelActionButton>
                  {editId ? (
                    <PanelActionButton
                      tone="secondary"
                      onClick={() => {
                        updateSearchParam(setSearchParams, "edit", null);
                        setForm(EMPTY_FORM);
                        setShowEventForm(false);
                      }}
                    >
                      Cancelar edicion
                    </PanelActionButton>
                  ) : null}
                </div>
              </form>
            )}
          </SectionPanel> : null}

          {showEventForm ? <SectionPanel
            title="Mis eventos"
            eyebrow="Seguimiento"
            description="Revise el estado de sus publicaciones y retome cualquier registro para editarlo."
          >
            {!user ? (
              <EmptyState
                icon={CalendarDays}
                title="Sin gestion activa"
                description="Ingrese con su cuenta para revisar o editar sus propios encuentros."
              />
            ) : !myEvents.length ? (
              <EmptyState
                icon={CalendarDays}
                title="Aun no ha publicado encuentros"
                description="Use el formulario superior para cargar la primera actividad de su proceso."
              />
            ) : (
              <div className="space-y-4">
                {myEvents.map((item) => (
                  <article key={item.id} className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex flex-wrap gap-2">
                          <StatusPill
                            tone={
                              item.estado === "PUBLICADO"
                                ? "positive"
                                : item.estado === "RECHAZADO"
                                  ? "danger"
                                  : "warning"
                            }
                          >
                            {item.estado}
                          </StatusPill>
                          <StatusPill tone="neutral">{getTypeLabel(item.tipo)}</StatusPill>
                        </div>
                        <h3 className="mt-3 font-heading text-xl font-semibold text-primary">{item.nombre}</h3>
                        <p className="mt-2 text-sm leading-6 text-mutedForeground">
                          {formatDateTime(item.fecha_inicio)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <PanelActionButton
                          tone="secondary"
                          onClick={() => {
                            setShowEventForm(true);
                            updateSearchParam(setSearchParams, "edit", item.id);
                          }}
                        >
                          <PencilLine className="h-4 w-4" />
                          Editar
                        </PanelActionButton>
                        <PanelActionButton
                          tone="danger"
                          disabled={deletingId === String(item.id)}
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          {deletingId === String(item.id) ? "Eliminando..." : "Eliminar"}
                        </PanelActionButton>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </SectionPanel> : null}
      </div>
    </div>
  );
}
